import { createAsyncThunk } from '@reduxjs/toolkit';
import { BaseApiResponse, ScenarioGroup } from 'common/types';
import { wrapRequest } from 'common/utils';
import { probeService as service } from 'services/serviceStorage';
import {
    ProbeFilter,
    ProbeGroupHistoryItem,
    ProbeGroupListItem,
    ProbeGroupPayload,
    ProbeHistoryItem,
    ProbeScenarioGroupItem,
    RunProbesPayload
} from './types';

export const fetchProbeGroupsSource = createAsyncThunk<{ data: ProbeGroupListItem[]; total: number }, ProbeFilter>(
    'probe/fetchProbeGroupsSource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: ProbeGroupListItem[]; totalCount: number; }>>({
            handler: () => service.getProbeGroups(filter),
            showNotifications: true,
        });

        return {
            data: data?.result.items.map((item) => ({
                ...item,
                idx: item.groupId,
                modified: item.modified ? new Date(item.modified) : null,
            })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const fetchScenarioGroupsSource = createAsyncThunk<{ data: ProbeScenarioGroupItem[]; total: number }, ProbeFilter>(
    'probe/fetchScenarioGroupsSource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: ScenarioGroup[]; totalCount: number }>>({
            handler: () => service.getScenarioGroups(filter),
            showNotifications: true,
        });

        return {
            data: data?.result.items.map((item) => ({ ...item, idx: item.groupId })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const saveProbeGroup = createAsyncThunk<number, ProbeGroupPayload>(
    'probe/saveProbeGroup',
    async (group) => {
        const { data } = await wrapRequest<BaseApiResponse<number>>({
            handler: () => service.saveProbeGroup(group),
            showNotifications: true,
        });

        return data!.result;
    }
);

export const uploadProbeFile = createAsyncThunk<void, { groupId: number; file: File }>(
    'probe/uploadProbeFile',
    async ({ groupId, file }, { rejectWithValue }) => {
        const response = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.uploadProbeFile(groupId, file),
            showNotifications: true,
        });

        if (response.errors?.length || response.warnings?.length) {
            return rejectWithValue(response);
        }
    }
);

export const deleteProbeGroups = createAsyncThunk<void, { probeGroupIds: number[]; }>(
    'probe/deleteProbeGroups',
    async (filter, { rejectWithValue }) => {
        const response = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteProbeGroups(filter),
            showNotifications: true,
        });

        if (response.errors?.length || response.warnings?.length) {
            return rejectWithValue(response);
        }
    }
);

export const runProbes = createAsyncThunk<void, RunProbesPayload>(
    'probe/runProbes',
    async (item, { rejectWithValue }) => {
        const response = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runProbes(item),
            showNotifications: true,
        });

        if (response.errors?.length || response.warnings?.length) {
            return rejectWithValue(response);
        }
    }
);

export const getProbesUrl = createAsyncThunk<string, number>(
    'probe/getProbesUrl',
    async (groupId, { rejectWithValue }) => {
        const response = await wrapRequest<BaseApiResponse<string>>({
            handler: () => service.getProbesUrl(groupId),
            showNotifications: true,
        });

        if (response.errors?.length || response.warnings?.length) {
            return rejectWithValue(response);
        }

        return response.data?.result || '';
    }
);

export const fetchProbeGroupsHistorySource = createAsyncThunk<{ data: ProbeGroupHistoryItem[]; total: number }, ProbeFilter>(
    'probe/fetchProbeGroupsHistorySource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: ProbeGroupHistoryItem[]; totalCount: number; }>>({
            handler: () => service.getProbeGroupsHistory(filter),
            showNotifications: true,
        });

        return {
            data: data?.result.items.map((item, index) => ({ ...item, idx: index })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const fetchProbesHistorySource = createAsyncThunk<{ data: ProbeHistoryItem[]; total: number }, ProbeFilter>(
    'probe/fetchProbesHistorySource',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: ProbeHistoryItem[]; totalCount: number; }>>({
            handler: () => service.getProbesHistory(filter),
            showNotifications: true,
        });

        return {
            data: data?.result.items.map((item) => ({ ...item, idx: item.historyId })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);
